import { XMarkIcon } from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import { Button, Text } from "north.js";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import Modal from "react-modal";
import styled from "styled-components";

import { errorToast } from "src/shared/components/Toast";
import { Route } from "src/shared/constants/route";
import {
  ProfileContextProvider,
  useProfileContext,
} from "src/shared/contexts/ProfileContext";
import { Nullable } from "src/shared/utils/typescript";
import { DiscoveryProfile } from "src/views/hacker-directory/shared/types";

import ProfileCell from "../ProfileCell";
import ProfileModal from "../ProfileModal";
import SkeletonProfileCell from "../SkeletonProfileCell";

import {
  readAutosuggestClaimIds,
  writeAutosuggestClaimIds,
} from "./autosuggestCache";
import DirectoryControls from "./DirectoryControls";
import DirectoryFilters, {
  ExpandedFilterSections,
  FilterSection,
  StagedFilters,
} from "./DirectoryFilters";
import { getAutosuggestTeammateCapacities } from "./teammateCapacity";
import useDiscoveryProfiles, { BATCH_SIZE } from "./useDiscoveryProfiles";

const DEFAULT_FILTERS: StagedFilters = {
  textQuery: "",
  interests: [],
  weekendPace: [],
  teammatesNeeded: [],
};

const DEFAULT_EXPANDED_FILTER_SECTIONS: ExpandedFilterSections = {
  lookingFor: false,
  weekendPace: false,
  projectInterests: false,
};

const FILTER_PANEL_WIDTH = 195;
const FILTER_STICKY_TOP = 92;
const FILTER_BOTTOM_GAP = 16;
const FILTER_NATURAL_TOP_FALLBACK = 254;
const MOBILE_FILTER_BREAKPOINT = "(max-width: 900px)";
const MOBILE_FILTER_ANIMATION_MS = 200;

const useMobileFilterLayout = (): boolean => {
  const [matches, setMatches] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(MOBILE_FILTER_BREAKPOINT).matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_FILTER_BREAKPOINT);
    const updateMatches = () => setMatches(mediaQuery.matches);

    updateMatches();
    mediaQuery.addListener(updateMatches);
    return () => mediaQuery.removeListener(updateMatches);
  }, []);

  return matches;
};

const Browser = styled.div`
  display: flex;
  flex-direction: column;
`;

const StickyBoundary = styled.div`
  width: 1px;
  height: 1px;
  margin-bottom: -1px;
  pointer-events: none;
`;

const StickyControls = styled.div`
  position: sticky;
  z-index: 2;
  top: 0;
  padding: 24px 0;
  background-color: ${({ theme }) => theme.globalConstants.color.white};
`;

const BrowserBody = styled.div`
  display: flex;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const Results = styled.div`
  min-width: 0;
  flex: 1 1 0%;
`;

const StickyFilters = styled(motion.div)<{
  $controlsStuck: boolean;
  $naturalTop: number;
}>`
  position: sticky;
  top: ${FILTER_STICKY_TOP}px;
  max-height: ${({ $controlsStuck, $naturalTop }) =>
    `calc(100vh - ${
      $controlsStuck ? FILTER_STICKY_TOP : $naturalTop
    }px - ${FILTER_BOTTOM_GAP}px)`};
  max-height: ${({ $controlsStuck, $naturalTop }) =>
    `calc(100dvh - ${
      $controlsStuck ? FILTER_STICKY_TOP : $naturalTop
    }px - ${FILTER_BOTTOM_GAP}px)`};
  flex-shrink: 0;
  align-self: flex-start;
  overflow: hidden;
`;

const MobileFilterOverlay = styled.div`
  opacity: 0;
  transition: opacity ${MOBILE_FILTER_ANIMATION_MS}ms ease;

  &.ReactModal__Overlay--after-open {
    opacity: 1;
  }

  &.ReactModal__Overlay--before-close {
    opacity: 0;
  }
`;

const MobileFilters = styled(Modal)`
  display: flex;
  box-sizing: border-box;
  width: 360px;
  max-width: 100vw;
  height: 100%;
  max-height: 100vh;
  flex-direction: column;
  padding: 24px;
  overflow: hidden;
  border-radius: 8px 0 0 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  outline: none;
  transform: translateX(100%);
  transition: transform ${MOBILE_FILTER_ANIMATION_MS}ms ease;

  &.ReactModal__Content--after-open {
    transform: translateX(0);
  }

  &.ReactModal__Content--before-close {
    transform: translateX(100%);
  }

  > aside {
    width: 100%;
    min-width: 0;
    min-height: 0;
    max-height: none;
    flex: 1 1 0%;
  }

  @media (max-width: 600px) {
    width: 100vw;
    border-radius: 0;
  }
`;

const MobileFilterHeader = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 24px;
`;

const MobileFilterCloseButton = styled.button`
  display: flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  column-gap: 16px;
  row-gap: 32px;
`;

const Sentinel = styled.div`
  width: 100%;
  height: 4px;
`;

const ResultsState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 64px 24px;
  border-radius: 16px;
  text-align: center;
`;

const SecondaryText = styled(Text)`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
`;

const EndOfResults = styled.div`
  display: flex;
  padding: 32px 16px 16px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  text-align: center;
`;

interface DiscoveryProfileResultsProps {
  filters: StagedFilters;
  autosuggestActive: boolean;
  defaultAutosuggestActive: boolean;
  onLoadingChange: (loading: boolean) => void;
  onDefaultAutosuggestUnavailable: () => void;
  onAutosuggestRateLimited: () => void;
  onProfileClick: (
    profile: DiscoveryProfile,
    event: React.MouseEvent<HTMLElement>
  ) => void;
}

const DiscoveryProfileResultsComponent = ({
  defaultAutosuggestActive,
  filters,
  autosuggestActive,
  onDefaultAutosuggestUnavailable,
  onAutosuggestRateLimited,
  onLoadingChange,
  onProfileClick,
}: DiscoveryProfileResultsProps) => {
  const [sentinelRef, sentinelInView] = useInView();
  // Loaded once for the directory; relationship state is derived per card from
  // this plus each hydrated profile's teammates_claim_ids (no per-card fetches).
  const {
    profile: authenticatedProfile,
    isLoading: authenticatedProfileLoading,
    loadError: authenticatedProfileError,
    refetch: refetchAuthenticatedProfile,
  } = useProfileContext();
  const profileIdsVariables = useMemo(() => {
    if (autosuggestActive && authenticatedProfile) {
      const bio = [
        authenticatedProfile.bio_prompt?.trim()
          ? `Prompt: ${authenticatedProfile.bio_prompt}`
          : null,
        authenticatedProfile.bio?.trim()
          ? `Response: ${authenticatedProfile.bio}`
          : null,
      ]
        .filter(Boolean)
        .join("\n");
      const semanticQuery = [
        bio ? `bio: ${JSON.stringify(bio)}` : null,
        authenticatedProfile.experience_goals?.trim()
          ? `experience_goals: ${JSON.stringify(
              authenticatedProfile.experience_goals
            )}`
          : null,
        authenticatedProfile.interests?.length
          ? `interests: ${JSON.stringify(authenticatedProfile.interests)}`
          : null,
        authenticatedProfile.skills?.length
          ? `skills: ${JSON.stringify(authenticatedProfile.skills)}`
          : null,
        authenticatedProfile.teammate_criteria?.trim()
          ? `teammate_criteria: ${JSON.stringify(
              authenticatedProfile.teammate_criteria
            )}`
          : null,
      ]
        .filter(Boolean)
        .join("\n");

      return {
        interests: [],
        weekendPace: [],
        teammatesNeeded: getAutosuggestTeammateCapacities(
          authenticatedProfile.teammates_needed ?? 0
        ),
        query: { semantic: semanticQuery },
      };
    }

    return {
      interests: filters.interests,
      weekendPace: filters.weekendPace,
      teammatesNeeded: filters.teammatesNeeded,
      query: filters.textQuery
        ? {
            lexical: filters.textQuery,
          }
        : undefined,
    };
  }, [authenticatedProfile, autosuggestActive, filters]);
  const autosuggestClaimId = autosuggestActive
    ? authenticatedProfile?.claimId
    : undefined;
  const cachedAutosuggestClaimIds = useMemo(
    () =>
      autosuggestClaimId === undefined
        ? null
        : readAutosuggestClaimIds(autosuggestClaimId),
    [autosuggestClaimId]
  );
  const cacheAutosuggestClaimIds = useCallback(
    (claimIds: number[]) => {
      if (autosuggestClaimId !== undefined) {
        writeAutosuggestClaimIds(autosuggestClaimId, claimIds);
      }
    },
    [autosuggestClaimId]
  );
  const { profiles, initial, batch, rateLimited, retryInitial, loadNextBatch } =
    useDiscoveryProfiles(profileIdsVariables, {
      skipProfileIdsQuery: autosuggestActive && !authenticatedProfile,
      cachedProfileIds:
        autosuggestClaimId === undefined ? null : cachedAutosuggestClaimIds,
      onProfileIdsLoaded:
        autosuggestClaimId === undefined ? undefined : cacheAutosuggestClaimIds,
    });
  const autosuggestRateLimited = autosuggestActive && rateLimited;
  const autosuggestUnavailable =
    autosuggestActive &&
    !authenticatedProfileLoading &&
    (authenticatedProfileError ||
      !authenticatedProfile ||
      (!initial.loading && initial.error && !autosuggestRateLimited));
  const shouldDisableDefaultAutosuggest =
    defaultAutosuggestActive && autosuggestUnavailable;

  const unobserveAndLoadNextBatch = useCallback(() => {
    sentinelRef(null);
    loadNextBatch();
  }, [loadNextBatch, sentinelRef]);

  useEffect(() => {
    if (sentinelInView && !batch.loading && batch.hasMore) {
      unobserveAndLoadNextBatch();
    }
  }, [batch.hasMore, batch.loading, sentinelInView, unobserveAndLoadNextBatch]);

  useEffect(() => {
    onLoadingChange(
      initial.loading || (autosuggestActive && authenticatedProfileLoading)
    );
  }, [
    authenticatedProfileLoading,
    autosuggestActive,
    initial.loading,
    onLoadingChange,
  ]);

  useLayoutEffect(() => {
    if (shouldDisableDefaultAutosuggest) onDefaultAutosuggestUnavailable();
  }, [onDefaultAutosuggestUnavailable, shouldDisableDefaultAutosuggest]);

  useLayoutEffect(() => {
    if (autosuggestRateLimited) onAutosuggestRateLimited();
  }, [autosuggestRateLimited, onAutosuggestRateLimited]);

  if (
    autosuggestActive &&
    (authenticatedProfileLoading ||
      shouldDisableDefaultAutosuggest ||
      autosuggestRateLimited)
  ) {
    return (
      <ProfileGrid>
        {Array.from({ length: BATCH_SIZE }, (_, index) => (
          <SkeletonProfileCell key={index} />
        ))}
      </ProfileGrid>
    );
  }

  if (autosuggestActive && authenticatedProfileError) {
    return (
      <ResultsState role="alert">
        <Text type="h3">Something went wrong</Text>
        <SecondaryText type="body1">
          We couldn&apos;t fetch your profile to curate hacker recommendations
          for you. Please try again.
        </SecondaryText>
        <Button
          color="primary"
          size="md"
          onClick={() => void refetchAuthenticatedProfile()}
        >
          Try again
        </Button>
      </ResultsState>
    );
  }

  if (autosuggestActive && !authenticatedProfile) {
    return (
      <ResultsState>
        <Text type="h3">Complete your profile to use Auto-Suggest</Text>
        <SecondaryText type="body1">
          Auto-Suggest uses your Hacker Directory profile to find strong
          matches.
        </SecondaryText>
      </ResultsState>
    );
  }

  return (
    <>
      {initial.loading && (
        <ProfileGrid>
          {Array.from({ length: BATCH_SIZE }, (_, index) => (
            <SkeletonProfileCell key={index} />
          ))}
        </ProfileGrid>
      )}

      {!initial.loading && initial.error && (
        <ResultsState role="alert">
          <Text type="h3">Something went wrong</Text>
          <SecondaryText type="body1">
            We couldn&apos;t load the hacker directory. Please try again.
          </SecondaryText>
          <Button color="primary" size="md" onClick={retryInitial}>
            Try again
          </Button>
        </ResultsState>
      )}

      {!initial.loading &&
        !initial.error &&
        profiles.length === 0 &&
        !batch.hasMore && (
          <ResultsState>
            <Text type="h3">No hackers found</Text>
            <SecondaryText type="body1">
              Try adjusting your search or filters to find more hackers.
            </SecondaryText>
          </ResultsState>
        )}

      {!initial.loading && !initial.error && profiles.length > 0 && (
        <>
          <ProfileGrid>
            {profiles.map((profile, index) => (
              <ProfileCell
                key={profile?.id ?? index}
                profile={profile}
                authenticatedClaimId={authenticatedProfile?.claimId}
                authenticatedTeammateClaimIds={
                  authenticatedProfile?.teammates_claim_ids
                }
                onClick={onProfileClick}
              />
            ))}
            {batch.loading &&
              Array.from({ length: batch.nextSize }, (_, index) => (
                <SkeletonProfileCell key={`batch-${index}`} />
              ))}
          </ProfileGrid>
          {!batch.loading && !batch.error && batch.hasMore && (
            <Sentinel ref={sentinelRef} />
          )}
          {batch.error && (
            <ResultsState role="alert">
              <Text type="h3">We couldn&apos;t load more hackers</Text>
              <SecondaryText type="body1">
                Try again to keep browsing the directory.
              </SecondaryText>
              <Button
                color="primary"
                size="md"
                onClick={unobserveAndLoadNextBatch}
              >
                Try again
              </Button>
            </ResultsState>
          )}
          {!batch.loading && !batch.error && !batch.hasMore && (
            <EndOfResults role="status" aria-live="polite">
              <SecondaryText type="body1">
                You&rsquo;ve reached the end!
              </SecondaryText>
              <SecondaryText type="body2">
                Try different search terms or filters to discover more people.
              </SecondaryText>
            </EndOfResults>
          )}
        </>
      )}
    </>
  );
};

const DiscoveryProfileResults = React.memo(DiscoveryProfileResultsComponent);

const DirectoryBrowserContent: React.FC = () => {
  const browserBodyRef = useRef<HTMLDivElement>(null);
  const stickyControlsRef = useRef<HTMLDivElement>(null);
  const initialRequestRef = useRef(true);
  const filtersOpenBeforeAutosuggestRef = useRef(false);
  const [stickyBoundaryRef, stickyBoundaryInView, stickyBoundaryEntry] =
    useInView({ initialInView: true, rootMargin: "-1px 0px 0px" });
  const [stagedFilters, setStagedFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [requestKey, setRequestKey] = useState(0);
  const [resultsLoading, setResultsLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [autosuggestActive, setAutosuggestActive] = useState(true);
  const [defaultAutosuggestActive, setDefaultAutosuggestActive] =
    useState(true);
  const [filterNaturalTop, setFilterNaturalTop] = useState(
    FILTER_NATURAL_TOP_FALLBACK
  );
  const [expandedFilterSections, setExpandedFilterSections] = useState(
    DEFAULT_EXPANDED_FILTER_SECTIONS
  );
  const mobileFilterLayout = useMobileFilterLayout();
  const [activeProfile, setActiveProfile] =
    useState<Nullable<DiscoveryProfile>>(null);
  const controlsStuck =
    !stickyBoundaryInView &&
    (stickyBoundaryEntry?.boundingClientRect.top ?? 0) < 0;

  useLayoutEffect(() => {
    const previousScrollRestoration = window.history.scrollRestoration;

    window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useEffect(() => {
    if (initialRequestRef.current) {
      initialRequestRef.current = false;
      return;
    }

    if (!browserBodyRef.current || !stickyControlsRef.current) return;

    const resultsTop =
      browserBodyRef.current.getBoundingClientRect().top + window.pageYOffset;
    const stickyControlsHeight =
      stickyControlsRef.current.getBoundingClientRect().height;

    window.scrollTo({
      top: Math.max(0, resultsTop - stickyControlsHeight),
      behavior: "smooth",
    });
  }, [requestKey]);

  // Measure the filters' natural position so DirectoryFilters remains fully
  // scrollable while the page header is still visible above it.
  useEffect(() => {
    const measureFilterNaturalTop = () => {
      if (!browserBodyRef.current) return;

      setFilterNaturalTop(
        browserBodyRef.current.getBoundingClientRect().top + window.pageYOffset
      );
    };

    measureFilterNaturalTop();
    window.addEventListener("resize", measureFilterNaturalTop);
    return () => window.removeEventListener("resize", measureFilterNaturalTop);
  }, []);

  const updateDirectoryFilters = (filters: StagedFilters) => {
    setStagedFilters(filters);
    setAppliedFilters((currentFilters) => ({
      ...currentFilters,
      interests: filters.interests,
      weekendPace: filters.weekendPace,
      teammatesNeeded: filters.teammatesNeeded,
    }));
    setResultsLoading(true);
    setRequestKey((currentKey) => currentKey + 1);
  };

  const applySearch = () => {
    setAppliedFilters({
      ...appliedFilters,
      textQuery: stagedFilters.textQuery,
    });
    setResultsLoading(true);
    setRequestKey((currentKey) => currentKey + 1);
  };

  const clearSearch = () => {
    setStagedFilters((currentFilters) => ({
      ...currentFilters,
      textQuery: "",
    }));
    setAppliedFilters((currentFilters) => ({
      ...currentFilters,
      textQuery: "",
    }));
    setResultsLoading(true);
    setRequestKey((currentKey) => currentKey + 1);
  };

  const closeFilters = () => {
    setStagedFilters({
      ...appliedFilters,
      textQuery: stagedFilters.textQuery,
    });
    setFiltersOpen(false);
  };

  const toggleFilters = () => {
    if (filtersOpen) closeFilters();
    else setFiltersOpen(true);
  };

  const deactivateAutosuggest = useCallback(() => {
    setAutosuggestActive(false);
    setFiltersOpen(filtersOpenBeforeAutosuggestRef.current);
    setResultsLoading(true);
    setRequestKey((currentKey) => currentKey + 1);
  }, []);
  const handleDefaultAutosuggestUnavailable = useCallback(() => {
    setDefaultAutosuggestActive(false);
    deactivateAutosuggest();
  }, [deactivateAutosuggest]);
  const handleAutosuggestRateLimited = useCallback(() => {
    errorToast(
      "You're sending requests too quickly. Auto-Suggest has been turned off. Please wait a moment and try again."
    );
    setDefaultAutosuggestActive(false);
    deactivateAutosuggest();
  }, [deactivateAutosuggest]);

  const toggleAutosuggest = () => {
    setDefaultAutosuggestActive(false);

    if (autosuggestActive) {
      deactivateAutosuggest();
      return;
    }

    filtersOpenBeforeAutosuggestRef.current = filtersOpen;
    if (filtersOpen) closeFilters();
    setAutosuggestActive(true);
    setResultsLoading(true);
    setRequestKey((currentKey) => currentKey + 1);
  };

  const toggleFilterSection = (section: FilterSection) => {
    setExpandedFilterSections((expandedSections) => ({
      ...expandedSections,
      [section]: !expandedSections[section],
    }));
  };

  const handleProfileClick = useCallback(
    (profile: DiscoveryProfile, event: React.MouseEvent<HTMLElement>) => {
      const profileUrl = `${Route.HACKER_DIRECTORY_PROFILE}?ref=${profile.id}`;

      if (event.ctrlKey || event.metaKey) {
        window.open(profileUrl, "_blank", "noopener,noreferrer");
        return;
      }

      setActiveProfile(profile);
    },
    []
  );

  useEffect(() => {
    if (!filtersOpen || !mobileFilterLayout) return;

    const body = document.body;
    const previousOverflow = body.style.overflow;
    const previousPaddingRight = body.style.paddingRight;
    const currentPaddingRight = parseFloat(
      window.getComputedStyle(body).paddingRight
    );
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;

    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPaddingRight;
    };
  }, [filtersOpen, mobileFilterLayout]);

  const activeFilterCount =
    appliedFilters.interests.length +
    appliedFilters.weekendPace.length +
    appliedFilters.teammatesNeeded.length;

  return (
    <Browser>
      <StickyBoundary ref={stickyBoundaryRef} aria-hidden="true" />
      <StickyControls ref={stickyControlsRef}>
        <DirectoryControls
          filters={stagedFilters}
          filtersOpen={filtersOpen}
          activeFilterCount={activeFilterCount}
          autosuggestActive={autosuggestActive}
          disabled={resultsLoading}
          onChange={setStagedFilters}
          onSearch={applySearch}
          onClearSearch={clearSearch}
          onToggleAutosuggest={toggleAutosuggest}
          onToggleFilters={toggleFilters}
        />
      </StickyControls>
      <BrowserBody ref={browserBodyRef}>
        <Results>
          <DiscoveryProfileResults
            key={requestKey}
            filters={appliedFilters}
            autosuggestActive={autosuggestActive}
            defaultAutosuggestActive={defaultAutosuggestActive}
            onLoadingChange={setResultsLoading}
            onDefaultAutosuggestUnavailable={
              handleDefaultAutosuggestUnavailable
            }
            onAutosuggestRateLimited={handleAutosuggestRateLimited}
            onProfileClick={handleProfileClick}
          />
        </Results>
        {!mobileFilterLayout && (
          <AnimatePresence initial={false}>
            {!autosuggestActive && filtersOpen && (
              <StickyFilters
                $controlsStuck={controlsStuck}
                $naturalTop={filterNaturalTop}
                initial={{ marginLeft: 0, opacity: 0, width: 0, x: 24 }}
                animate={{
                  marginLeft: 32,
                  opacity: 1,
                  width: FILTER_PANEL_WIDTH,
                  x: 0,
                }}
                exit={{ marginLeft: 0, opacity: 0, width: 0, x: 24 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
              >
                <DirectoryFilters
                  filters={stagedFilters}
                  expandedSections={expandedFilterSections}
                  onChange={updateDirectoryFilters}
                  onToggleSection={toggleFilterSection}
                />
              </StickyFilters>
            )}
          </AnimatePresence>
        )}
      </BrowserBody>
      {mobileFilterLayout && (
        <MobileFilters
          isOpen={!autosuggestActive && filtersOpen}
          closeTimeoutMS={MOBILE_FILTER_ANIMATION_MS}
          onRequestClose={closeFilters}
          shouldCloseOnEsc
          shouldCloseOnOverlayClick
          shouldReturnFocusAfterClose
          preventScroll
          contentLabel="Directory filters"
          overlayElement={(props, contentElement) => (
            <MobileFilterOverlay {...props}>
              {contentElement}
            </MobileFilterOverlay>
          )}
          style={{
            overlay: {
              position: "fixed",
              zIndex: 1000,
              inset: 0,
              display: "flex",
              alignItems: "stretch",
              justifyContent: "flex-end",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
            },
          }}
        >
          <MobileFilterHeader>
            <Text type="h3">Filters</Text>
            <MobileFilterCloseButton
              type="button"
              aria-label="Close filters"
              onClick={closeFilters}
            >
              <XMarkIcon aria-hidden="true" />
            </MobileFilterCloseButton>
          </MobileFilterHeader>
          <DirectoryFilters
            filters={stagedFilters}
            expandedSections={expandedFilterSections}
            onChange={updateDirectoryFilters}
            onToggleSection={toggleFilterSection}
          />
        </MobileFilters>
      )}
      {activeProfile && (
        <ProfileModal
          profile={activeProfile}
          onClose={() => setActiveProfile(null)}
        />
      )}
    </Browser>
  );
};

const DirectoryBrowser: React.FC = () => (
  <ProfileContextProvider>
    <DirectoryBrowserContent />
  </ProfileContextProvider>
);

export default DirectoryBrowser;
